import type { Candidate, Job } from '@core/types';
import type { GapAnalysis, JDStructure } from '@soVendor/utils/jdAnalysis';
import { extractJDStructure, analyzeGaps } from '@soVendor/utils/jdAnalysis';
import { analyzeATSCompliance } from '@soVendor/utils/atsComplianceAnalyzer';
import { buildCandidateResumeText } from './buildCandidateResumeText';
import { b2bJobToOptimizerJob } from './b2bJobToOptimizerJob';

export interface RoleFitRow {
  key: string;
  candidate: Candidate;
  job: Job;
  jd: JDStructure;
  gap: GapAnalysis;
  fitmentScore: number;
  coverageScore: number;
  atsOverall: number;
  atsRating: 'Poor' | 'Fair' | 'Good' | 'Excellent';
  atsSections: { formatting: number; keywords: number; structure: number; content: number };
}

function atsBand(score: number): RoleFitRow['atsRating'] {
  if (score >= 85) return 'Excellent';
  if (score >= 70) return 'Good';
  if (score >= 50) return 'Fair';
  return 'Poor';
}

/**
 * Combines Strategic Alignment coverage (jdAnalysis) with ATS keyword/structure
 * scoring — same building blocks used in Single Optimize before LLM rewrite.
 */
export function computeRoleFitPair(candidate: Candidate, job: Job): RoleFitRow {
  const resumeText = buildCandidateResumeText(candidate);
  const jobData = b2bJobToOptimizerJob(job);
  const jd = extractJDStructure(jobData);
  const gap = analyzeGaps(resumeText, jd);
  const ats = analyzeATSCompliance(resumeText, jobData);

  const fitmentScore = Math.min(
    100,
    Math.max(
      0,
      Math.round(gap.coverageScore * 0.55 + ats.overallScore * 0.45),
    ),
  );

  return {
    key: `${candidate.id}:${job.id}`,
    candidate,
    job,
    jd,
    gap,
    fitmentScore,
    coverageScore: gap.coverageScore,
    atsOverall: ats.overallScore,
    atsRating: atsBand(ats.overallScore),
    atsSections: ats.sections,
  };
}
